// Change #100462 - switch an existing company between Standard and Enterprise.
// Used by Back End > Company Report and Company Details > Subscriptions.
//
// To Enterprise: the plan is created (based on a master product, with the
// bespoke price, Change #100463). Everyone holding a licence of a ticked
// product is moved onto a free seat and those licences end now, so nobody
// loses access and nothing is charged by card again. Unticked products
// (bolt-ons such as SIMs or WhatsApp) carry on as they are.
// To Standard: the free seats end on a chosen date so the customer has time to
// buy licences by card before anyone loses access.

import React, { useEffect, useState } from "react";
import { Input } from "reactstrap";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import EnterprisePlanFields, {
  enterprisePlanPayload,
  validateEnterprisePlan,
} from "../../../../Components/Common/EnterprisePlanFields";
import {
  enterprisePlanAdminCompanyApi,
  enterprisePlanAdminSetModeApi,
} from "../../../../helpers/fakebackend_helper";

const ENTERPRISE = "ENTERPRISE";

const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function BillingModeModal({ company, onClose, onDone }) {
  const toEnterprise = company?.mode !== ENTERPRISE;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [licenceProducts, setLicenceProducts] = useState([]);
  const [replace, setReplace] = useState({});
  const [priceReady, setPriceReady] = useState(false);

  useEffect(() => {
    if (!company) return;
    setForm({
      PlanName: "",
      MaxUsers: "",
      MaxUsersTolerance: "",
      BaseProduct_Id: "",
      PriceLocalSubTotal: "",
      PricePeriod: "MONTHLY",
      RrpLocalSubTotal: "",
      // Change #100463 - the plan fee bills like any subscription: ask when.
      needsFeeDates: true,
      FeeStartDate: "",
      FeeRenewalDate: "",
      SeatsEndDate: inDays(30),
    });
    setProducts([]);
    setLicenceProducts([]);
    setReplace({});
    if (company?.mode === ENTERPRISE) return;
    setLoading(true);
    enterprisePlanAdminCompanyApi(company.Id)
      .then((res) => {
        if (res?.status !== 200) return;
        const list = res.subscriptionproduct_list || [];
        setProducts(list);
        setPriceReady(Boolean(res.price_ready));
        const lp = res.licenceproduct_list || [];
        setLicenceProducts(lp);
        const tick = {};
        lp.forEach((p) => {
          if (p.Replace) tick[String(p.SubscriptionProduct_Id)] = true;
        });
        setReplace(tick);
        // Default the base to the master Enterprise (Monthly) product if present.
        const ent = list.find((p) => /enterprise/i.test(p.Name || "") && String(p.SubscriptionLicenceDurationType_Id) === "1");
        if (ent) setForm((f) => ({ ...f, BaseProduct_Id: String(ent.Id) }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [company]);

  const save = async () => {
    if (!company) return;
    let body;
    if (toEnterprise) {
      const problem = validateEnterprisePlan(form);
      if (problem) {
        toast.error(problem);
        return;
      }
      body = {
        Company_Id: company.Id,
        SubscriptionBillingMode: ENTERPRISE,
        ...enterprisePlanPayload(form),
        ReplaceProduct_Ids: Object.keys(replace)
          .filter((k) => replace[k])
          .map(Number),
      };
    } else {
      if (!form.SeatsEndDate) {
        toast.error("Pick when the Enterprise seats end");
        return;
      }
      body = { Company_Id: company.Id, SubscriptionBillingMode: "STANDARD", SeatsEndDate: form.SeatsEndDate };
    }
    setSaving(true);
    try {
      const res = await enterprisePlanAdminSetModeApi([body]);
      if (res?.status === 200) {
        toast.success(res.message || "Billing updated");
        onDone && onDone();
      } else {
        toast.error(res?.message || "Could not change the billing");
      }
    } catch (e) {
      toast.error("Could not change the billing");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      open={Boolean(company)}
      onClose={() => !saving && onClose()}
      title={toEnterprise ? "Switch to Enterprise" : "Switch to Standard"}
      subtitle={company?.BusinessName}
      icon="ri-shield-star-line"
      headerAccent="indigo"
      size={toEnterprise ? "md" : "sm"}
      onSubmit={save}
      submitLabel={toEnterprise ? "Make Enterprise" : "Make Standard"}
      submitDisabled={saving || loading}
      loading={saving}
      loadingText="Switching..."
    >
      {company && toEnterprise && (
        <>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="d-flex flex-column gap-3">
              <EnterprisePlanFields
                value={form}
                onChange={setForm}
                products={products}
                businessName={company.BusinessName}
                priceReady={priceReady}
              />
              {licenceProducts.length > 0 && (
                <div>
                  <label className="form-label mb-1">The plan replaces</label>
                  <div className="border rounded-3 p-2 d-flex flex-column gap-1">
                    {licenceProducts.map((p) => {
                      const k = String(p.SubscriptionProduct_Id);
                      return (
                        <label key={k} className="d-flex align-items-center gap-2 mb-0 fs-13" style={{ cursor: "pointer" }}>
                          <Input
                            type="checkbox"
                            className="mt-0"
                            checked={!!replace[k]}
                            onChange={(e) => setReplace({ ...replace, [k]: e.target.checked })}
                          />
                          <span className="flex-grow-1">{p.Name}</span>
                          <span className="text-muted">
                            {p.Assigned} of {p.Qty} assigned
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="text-muted fs-12 mt-1">
                    Ticked: their users move onto the plan and those card licences end now. Unticked (bolt-ons
                    like SIMs) carry on and keep billing as they are.
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {company && !toEnterprise && (
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label mb-1">
              Enterprise seats end on <span className="text-danger">*</span>
            </label>
            <Input
              type="date"
              value={form.SeatsEndDate || ""}
              onChange={(e) => setForm({ ...form, SeatsEndDate: e.target.value })}
            />
          </div>
          <div className="col-12">
            <div className="alert alert-warning fs-13 mb-0">
              The company goes back to buying licences by card. Their users keep access until this date; after it,
              they need card licences to keep using CRMit.
            </div>
          </div>
        </div>
      )}
    </AppModal>
  );
}
